/**
 * The machine at the shop that holds the vendor session.
 *
 * The shop pays for ONE seat, and the cure for the refusal that stopped lookups is
 * one browser window on a computer at the shop that stays open and stays signed in.
 * This is how that machine is introduced to the server and watched afterwards.
 *
 * `apiRoot`, not `api`: the routes are mounted at `/api/alldata/agents`, and `api`
 * points at `/api/admin`.
 */

import { apiRoot } from '../apiInstances';

/**
 * What the machine is doing, in the server's words.
 *
 * Deliberately not a boolean. "Paired" and "would a lookup run right now" are
 * different questions, and every state below is a different thing for someone to do
 * about it.
 */
export type MachineState =
  /** Connected, browser up, signed in. A lookup would run. */
  | 'ready'
  /** Running a job. The next one waits. */
  | 'busy'
  /** Connected, browser up, session expired — someone must sign in on that machine. */
  | 'needs_login'
  /** The agent is running but its browser is gone. */
  | 'chrome_down'
  /** No heartbeat. Unplugged, asleep, or off the network. */
  | 'offline'
  /** The vendor refused us recently; dispatch is paused rather than retried. */
  | 'blocked_by_vendor'
  /** This machine's token was taken away. Rows only — never the live state. */
  | 'revoked';

export interface LookupMachine {
  id: number;
  name: string;
  createdAt: string;
  lastSeenAt: string | null;
  lastIp: string | null;
  allowedCidr: string | null;
  enforceIp: boolean;
  agentBuild: string | null;
  state: MachineState;
  chromeOk: boolean;
  signedIn: boolean;
  revokedAt: string | null;
}

export interface MachineSummary {
  state: MachineState;
  agentId: number | null;
  name: string | null;
  currentJobId: string | null;
  lastSeenAt: number | null;
}

export interface PairingCode {
  code: string;
  expiresAt: string;
}

export const repairDataAgentApi = {
  async list(): Promise<{ machine: MachineSummary; agents: LookupMachine[] }> {
    const { data } = await apiRoot.get('/alldata/agents');
    return data;
  },

  /**
   * Mint a code to type on the shop machine.
   *
   * Single use, and short lived. It is shown once here and never stored anywhere
   * readable, so a code that scrolls away is replaced rather than recovered.
   */
  async createPairingCode(): Promise<PairingCode> {
    const { data } = await apiRoot.post('/alldata/agents/pair-code', {});
    return data;
  },

  /** A new token for the same machine. The old one stops working immediately. */
  async rotate(agentId: number): Promise<{ token: string }> {
    const { data } = await apiRoot.post(`/alldata/agents/${agentId}/rotate`, {});
    return data;
  },

  /** Take the machine away. The socket drops and the token dies. */
  async revoke(agentId: number): Promise<void> {
    await apiRoot.post(`/alldata/agents/${agentId}/revoke`, {});
  },
};

export default repairDataAgentApi;
